/**
 * Menu Configuration Factories
 *
 * Create configuration objects for MainMenu and ClueMenu.
 */

import { appState } from '../state/AppState';
import { session } from './puzzleSession';
import {
    getCellOperationContext,
    getEditorOperationContext,
    getViewOperationContext,
    getDialogOperationContext,
} from './operationContexts';
import { savePuzzle } from './saveWarning';
import { updateHistoryBar, updateMainMenu } from './menuUpdates';

// Import TypeScript operations
import {
    setColored,
    setBridge,
    setEmpty,
    setImage,
    setBlack,
    setVerticalLine,
    setHorizontalLine,
    setKnown,
} from '../operations/CellOperations';
import {
    changeDirection,
    toggleSyncEditing,
} from '../operations/EditorOperations';
import {
    toggleKnownMarking,
    toggleClueMarking,
    toggleMaskMarking,
} from '../operations/ViewOperations';
import {
    showInformation,
    showSearch,
    showVerification,
    unverify,
    showMaskUpload,
    showColorScheme,
} from '../operations/DialogOperations';

/**
 * Create the MainMenu configuration object
 */
export function createMainMenuConfig(isBuilderMode: boolean) {
    return {
        context: {
            getIsSaved: () => appState.isSaved,
            getIsVerified: () => appState.isVerified,
            getIsBuilderMode: () => appState.isBuilderMode,
            getSyncEditing: () => appState.syncEditing,
            getIsHorizontal: () => appState.isHorizontal,
            getKnownMarking: () => appState.knownMarking,
            getClueMarking: () => appState.clueMarking,
            getMaskMarking: () => appState.maskMarking,
            getImagePath: () => appState.imagePath,
            getColoredBase64: () => appState.coloredBase64,
            getBridgeBase64: () => appState.bridgeBase64,
            getCurrentElement: () => appState.currentElement,
            getAllowImages: () => appState.allowImages,
            getHasGraphics: () => appState.hasGraphics,
            getAllowColored: () => appState.allowColored,
            getAllowBridge: () => appState.allowBridge,
            getAllowEmpty: () => appState.allowEmpty,
            getAllowBlack: () => appState.allowBlack,
            getHasMultipleGrids: () => appState.hasMultipleGrids,
        },
        history: {
            hasUndo: () => session.historyManager?.canUndo ?? false,
            getUndoTooltip: () => session.historyManager?.undoLabel ?? '',
            hasRedo: () => session.historyManager?.canRedo ?? false,
            getRedoTooltip: () => session.historyManager?.redoLabel ?? '',
        },
        cell: {
            canBeBlack: () => {
                const cell = session.currentPuzzle?.getCell(
                    appState.currentCellId,
                );
                return cell
                    ? !cell.isKnown &&
                          !cell.hasVerticalLine &&
                          !cell.hasHorizontalLine
                    : false;
            },
            isBlack: () =>
                session.currentPuzzle?.getCell(appState.currentCellId)
                    ?.isBlack ?? false,
            canBeImage: () => {
                const cell = session.currentPuzzle?.getCell(
                    appState.currentCellId,
                );
                return cell
                    ? !cell.isKnown &&
                          !cell.hasVerticalLine &&
                          !cell.hasHorizontalLine
                    : false;
            },
            isImage: () =>
                session.currentPuzzle?.getCell(appState.currentCellId)
                    ?.isImage ?? false,
            canBeEmpty: () => {
                const cell = session.currentPuzzle?.getCell(
                    appState.currentCellId,
                );
                return cell ? !cell.isKnown : false;
            },
            isEmpty: () =>
                session.currentPuzzle?.getCell(appState.currentCellId)
                    ?.isEmpty ?? false,
            canBeColored: () => {
                const cell = session.currentPuzzle?.getCell(
                    appState.currentCellId,
                );
                return cell
                    ? !cell.isBlack && !cell.isImage && !cell.isEmpty
                    : false;
            },
            isColored: () =>
                session.currentPuzzle?.getCell(appState.currentCellId)
                    ?.isColored ?? false,
            canBeBridge: () => {
                const cell = session.currentPuzzle?.getCell(
                    appState.currentCellId,
                );
                return cell
                    ? !cell.isBlack && !cell.isImage && !cell.isEmpty
                    : false;
            },
            isBridge: () =>
                session.currentPuzzle?.getCell(appState.currentCellId)
                    ?.isBridge ?? false,
            canToggleVerticalLine: () => {
                const cell = session.currentPuzzle?.getCell(
                    appState.currentCellId,
                );
                return cell ? cell.verticalLineEnabled && !cell.isBlack : false;
            },
            hasVerticalLine: () =>
                session.currentPuzzle?.getCell(appState.currentCellId)
                    ?.hasVerticalLine ?? false,
            canToggleHorizontalLine: () => {
                const cell = session.currentPuzzle?.getCell(
                    appState.currentCellId,
                );
                return cell
                    ? cell.horizontalLineEnabled && !cell.isBlack
                    : false;
            },
            hasHorizontalLine: () =>
                session.currentPuzzle?.getCell(appState.currentCellId)
                    ?.hasHorizontalLine ?? false,
            canBeKnown: isBuilderMode
                ? () => {
                      if (!appState.isBuilderMode) return false;
                      const cell = session.currentPuzzle?.getCell(
                          appState.currentCellId,
                      );
                      if (!cell) return false;
                      const regex = new RegExp(appState.validCharPattern, 'g');
                      return regex.test(cell.value || '');
                  }
                : () => false,
            isKnown: () =>
                session.currentPuzzle?.getCell(appState.currentCellId)
                    ?.isKnown ?? false,
        },
        // Puzzle operations
        onSave: () => savePuzzle(),
        onUndo: () => {
            session.historyManager?.undo();
            updateHistoryBar();
            updateMainMenu();
        },
        onRedo: () => {
            session.historyManager?.redo();
            updateHistoryBar();
            updateMainMenu();
        },
        onVerify: () => {
            const ctx = getDialogOperationContext();
            if (ctx) showVerification(ctx);
        },
        onUnverify: () => {
            const ctx = getDialogOperationContext();
            if (ctx) unverify(ctx);
        },
        // Cell operations
        onSetColored: (el: HTMLElement) => {
            const ctx = getCellOperationContext();
            if (ctx) setColored(el, ctx);
        },
        onSetBridge: (el: HTMLElement) => {
            const ctx = getCellOperationContext();
            if (ctx) setBridge(el, ctx);
        },
        onSetEmpty: (el: HTMLElement) => {
            const ctx = getCellOperationContext();
            if (ctx) setEmpty(el, ctx);
        },
        onSetImage: (el: HTMLElement) => {
            const ctx = getCellOperationContext();
            if (ctx) setImage(el, ctx);
        },
        onSetBlack: (el: HTMLElement) => {
            const ctx = getCellOperationContext();
            if (ctx) setBlack(el, ctx);
        },
        onSetVerticalLine: (el: HTMLElement) => {
            const ctx = getCellOperationContext();
            if (ctx) setVerticalLine(el, ctx);
        },
        onSetHorizontalLine: (el: HTMLElement) => {
            const ctx = getCellOperationContext();
            if (ctx) setHorizontalLine(el, ctx);
        },
        // Builder-only cell operations
        ...(isBuilderMode
            ? {
                  onSetKnown: (el: HTMLElement) => {
                      const ctx = getCellOperationContext();
                      if (ctx) setKnown(el, ctx);
                  },
              }
            : {}),
        // Editor operations
        onToggleSync: (el: HTMLElement) => {
            const ctx = getEditorOperationContext();
            if (ctx) toggleSyncEditing(el, ctx);
        },
        onToggleDirection: (el: HTMLElement) => {
            const ctx = getEditorOperationContext();
            if (ctx) changeDirection(el, ctx);
        },
        // View operations
        onToggleKnownMarking: (el: HTMLElement) => {
            const ctx = getViewOperationContext();
            if (ctx) toggleKnownMarking(el, ctx);
        },
        onToggleClueMarking: (el: HTMLElement) => {
            const ctx = getViewOperationContext();
            if (ctx) toggleClueMarking(el, ctx);
        },
        onToggleMaskMarking: (el: HTMLElement) => {
            const ctx = getViewOperationContext();
            if (ctx) toggleMaskMarking(el, ctx);
        },
        // Graphics operations
        onChangeMask: () => {
            const ctx = getDialogOperationContext();
            if (ctx) showMaskUpload(ctx);
        },
        // Builder-only operations
        ...(isBuilderMode
            ? {
                  onSearch: () => {
                      const ctx = getDialogOperationContext();
                      if (ctx) showSearch(ctx);
                  },
                  onChangeColorScheme: () => {
                      const ctx = getDialogOperationContext();
                      if (ctx) showColorScheme(ctx);
                  },
              }
            : {}),
        // Other operations
        onShowInfo: () => {
            const ctx = getDialogOperationContext();
            if (ctx) showInformation(ctx);
        },
    };
}

/**
 * Create the ClueMenu configuration object
 */
export function createClueMenuConfig(onClueChange?: () => void) {
    return {
        getIsHorizontal: () => appState.isHorizontal,
        setIsHorizontal: (v: boolean) => {
            appState.isHorizontal = v;
        },
        getImagePath: () => appState.imagePath,
        getCurrentCell: () =>
            session.currentPuzzle?.getCell(appState.currentCellId) ?? null,
        getFirstCell: () => {
            const selection = appState.currentSelection;
            if (selection.length > 0) {
                return session.currentPuzzle?.getCell(selection[0]) ?? null;
            }
            return (
                session.currentPuzzle?.getCell(appState.currentCellId) ?? null
            );
        },
        getSelection: () => appState.currentSelection,
        getCurrentCellId: () => appState.currentCellId,
        getCellById: (cellId: number) =>
            session.currentPuzzle?.getCell(cellId) ?? null,
        isInChannel: () => {
            const grid = session.currentPuzzle?.getGrid(appState.currentCellId);
            if (!grid) return false;
            return appState.isHorizontal
                ? grid.isHorizontalChannel(appState.currentCellId)
                : grid.isVerticalChannel(appState.currentCellId);
        },
        ...(onClueChange ? { onClueChange } : {}),
    };
}
